import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { Grip } from 'lucide-react';
import { useContext } from 'react';
import { useSwipeable } from 'react-swipeable';
import Image from '@/common/Image';
import EventName from '~/constants/vdp/EventName';
import GalleryType from '~/constants/vdp/GalleryType';
import Section from '~/constants/vdp/section';
import CarouselContext from '~/context/vdp/CarouselContext';
import useGalleryImages from '~/hooks/vdp/useGalleryImages';
import optimizeImage from '~/utilities/optimizeImage';
import {
  Direction,
  INITIAL_LOAD_COUNT,
  SCROLL_INTERVAL,
  SNAP_INTERVAL,
  VISIBLE_SLIDE_COUNT
} from './constants';
import {
  GalleryContainer,
  GalleryScrollContainer,
  NavButton,
  PhotosAnnotation
} from './Gallery.styles';
import ScrollBar from './ScrollBar';
import useGalleryScroll from './useGalleryScroll';
import { Action, ActionTaken, elementEngaged } from '../Analytics';

export const IMAGE_ID_PREFIX = 'slide-image';

// Calculate image width based on visible slide count
// Formula: (100% / visibleSlides) - (gap * (visibleSlides - 1) / visibleSlides)
const gapSize = 16; // gap-xl
const imageWidthPercent = 100 / VISIBLE_SLIDE_COUNT;
const gapAdjustment =
  (gapSize * (VISIBLE_SLIDE_COUNT - 1)) / VISIBLE_SLIDE_COUNT;
const imageWidth = `calc(${imageWidthPercent}% - ${gapAdjustment}px)`;

export default function ScrollableGallery() {
  const { openFullScreenGallery } = useContext(CarouselContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const images = useGalleryImages();

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      trackEvent(elementEngaged, {
        action: Action.Swiped,
        actionTaken: ActionTaken.Left,
        galleryType: GalleryType.Tile,
        isModal: false,
        item: 'tile',
        section: Section.Gallery
      });
    },
    onSwipedRight: () => {
      trackEvent(elementEngaged, {
        action: Action.Swiped,
        actionTaken: ActionTaken.Right,
        galleryType: GalleryType.Tile,
        isModal: false,
        item: 'tile',
        section: Section.Gallery
      });
    },
    trackMouse: true,
    trackTouch: true
  });

  const {
    galleryRef,
    scrollPercent,
    thumbWidth,
    isDragging,
    shouldLoadAllImages,
    showLeftButton,
    showRightButton,
    handleScrollPercentChange,
    handleDragStart,
    handleDragEnd,
    scrollToSnapPoint
  } = useGalleryScroll({
    imagesCount: images.length,
    snapInterval: SNAP_INTERVAL,
    scrollInterval: SCROLL_INTERVAL
  });

  return (
    <GalleryContainer>
      <div className="relative" {...swipeHandlers}>
        <GalleryScrollContainer isDragging={isDragging} ref={galleryRef}>
          {images.map(({ id, imageUrl, type }, index) => {
            const optimizedImageUrl = optimizeImage(imageUrl, { width: 180 });
            const isSnapPoint = index % SNAP_INTERVAL === 0;
            const shouldLoad =
              shouldLoadAllImages || index < INITIAL_LOAD_COUNT;

            return optimizedImageUrl ? (
              <Clickable
                key={id}
                className={cx('relative aspect-[4/3] shrink-0', {
                  'snap-start': isSnapPoint
                })}
                data-adobe-id={`gallery-select-image-${index + 1}`}
                data-testid="gallery-image"
                data-analytics-attributes={{
                  galleryType: GalleryType.Tile,
                  imageType: type,
                  isModal: false,
                  item: 'image',
                  section: Section.Gallery
                }}
                data-analytics-id={elementEngaged}
                id={`${IMAGE_ID_PREFIX}-${id}`}
                onClick={() => openFullScreenGallery(id, type)}
                style={{ flexBasis: imageWidth }}
              >
                {shouldLoad ? (
                  <Image
                    alt={`Vehicle image ${index + 1}`}
                    className="absolute inset-0 h-full w-full rounded-md object-cover"
                    src={optimizedImageUrl}
                  />
                ) : (
                  <div className="absolute inset-0 h-full w-full rounded-md bg-background-muted-subtle" />
                )}
              </Clickable>
            ) : null;
          })}
        </GalleryScrollContainer>
        <NavButton
          aria-label="Previous image"
          isVisible={showLeftButton}
          direction="left"
          data-adobe-id="gallery-previous"
          data-testid="gallery-nav-prev"
          onClick={() => {
            scrollToSnapPoint(Direction.Prev);
            trackEvent(elementEngaged, {
              action: Action.Clicked,
              actionTaken: ActionTaken.Previous,
              galleryType: GalleryType.Tile,
              isModal: false,
              item: 'arrow button',
              section: Section.Gallery
            });
            trackEvent(EventName.galleryEngaged, {
              galleryType: GalleryType.Tile
            });
          }}
        />
        <NavButton
          aria-label="Next image"
          isVisible={showRightButton}
          direction="right"
          data-adobe-id="gallery-next"
          data-testid="gallery-nav-next"
          onClick={() => {
            scrollToSnapPoint(Direction.Next);
            trackEvent(elementEngaged, {
              action: Action.Clicked,
              actionTaken: ActionTaken.Next,
              galleryType: GalleryType.Tile,
              isModal: false,
              item: 'arrow button',
              section: Section.Gallery
            });
            trackEvent(EventName.galleryEngaged, {
              galleryType: GalleryType.Tile
            });
          }}
        />
        {images.length > 1 ? (
          <PhotosAnnotation>
            <Grip aria-hidden="true" size={16} />
            <span>{images.length.toLocaleString()} photos</span>
          </PhotosAnnotation>
        ) : null}
      </div>

      <ScrollBar
        scrollPercent={scrollPercent}
        onScrollPercentChange={handleScrollPercentChange}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        thumbWidth={thumbWidth}
      />
    </GalleryContainer>
  );
}
